import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { SEOHead } from "@/components/SEOHead";
import { NewHeroSection } from "@/components/sections/NewHeroSection";
import { MarqueeBanner } from "@/components/sections/MarqueeBanner";
import { FeaturedInMarquee } from "@/components/sections/FeaturedInMarquee";
import { PainSection } from "@/components/sections/PainSection";
import { SolutionSection } from "@/components/sections/SolutionSection";
import { NewFooterSection } from "@/components/sections/NewFooterSection";
import { LoadingScreen } from "@/components/LoadingScreen";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight } from "lucide-react";
import { WhitepaperPopup } from "@/components/WhitepaperPopup";
import { EcosystemGraphic } from "@/components/EcosystemGraphic";
import { CaseTeaserSection } from "@/components/sections/CaseTeaserSection";
import { WhitepaperTeaserSection } from "@/components/sections/WhitepaperTeaserSection";

const VALID_LANGS = ["de", "en"];

function TeaserCard({ titleKey, descKey, href }: { titleKey: string; descKey: string; href: string }) {
  const { t, localizedPath } = useLanguage();
  return (
    <Link
      to={localizedPath(href)}
      className="group block border-0 p-8 md:p-10 bg-background transition-colors duration-300"
    >
      <h3 className="text-lg md:text-xl font-medium tracking-tight group-hover:text-foreground transition-colors">
        {t(titleKey)}
      </h3>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
        {t(descKey)}
      </p>
      <span className="inline-flex items-center gap-1.5 mt-6 text-xs uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors">
        {t("teaser.cta")} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

const Index = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { lang } = useParams();
  const { language, t, localizedPath } = useLanguage();

  if (lang && !VALID_LANGS.includes(lang)) {
    return <Navigate to="/de" replace />;
  }

  const seoTitle = language === "de"
    ? "reelistiq — Micro Drama Agentur für Brands | Deutschland"
    : "reelistiq — Micro Drama Agency for Brands | Germany";

  const seoDesc = language === "de"
    ? "Deutschlands erste Micro Drama Agentur: Wir bringen Marken in serielle Kurzformate auf TikTok und Instagram — Brand Integration, Custom IP und Full Production."
    : "Germany's first Micro Drama agency: we bring brands into serialized short-form storytelling on TikTok and Instagram — brand integration, custom IP and full production.";

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        ogUrl={`https://reelistiq.com/${language}`}
        canonicalPath="/"
      />
      {isLoading && <LoadingScreen onLoadComplete={() => setIsLoading(false)} />}
      <Header />
      <main role="main">
        <NewHeroSection />
        <FeaturedInMarquee />
        <MarqueeBanner />
        <PainSection />
        <SolutionSection />

        {/* Ecosystem Section */}
        <section className="py-32 md:py-40 px-6">
          <div className="max-w-[1400px] mx-auto">
            <ScrollReveal>
              <div className="text-center mb-16">
                <h3 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                  {t("offer.ecosystem.headline")}
                </h3>
                <p className="mt-4 text-body-md text-muted-foreground max-w-2xl mx-auto">
                  {t("offer.ecosystem.subline")}
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <EcosystemGraphic />
            </ScrollReveal>
          </div>
        </section>

        <CaseTeaserSection />
        <WhitepaperTeaserSection />


        {/* Get Started CTA */}
        <section className="px-6 pb-32 md:pb-40">
          <div className="max-w-[1400px] mx-auto">
            <ScrollReveal>
              <div className="py-20 md:py-28 text-center">
                <h3 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                  {t("offer.getStarted.headline")}
                </h3>
                <p className="mt-4 text-body-md text-muted-foreground max-w-md mx-auto">
                  {t("offer.getStarted.subline")}
                </p>
                <Link
                  to={localizedPath("/contact")}
                  className="inline-flex items-center mt-10 px-10 py-4 rounded-full bg-accent text-accent-foreground text-sm font-medium uppercase tracking-wider hover:opacity-90 transition-opacity"
                >
                  {t("offer.getStarted.button")} →
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Teaser Grid to Subpages */}
        <section className="py-32 md:py-40 px-6">
          <div className="max-w-[1400px] mx-auto">
            <ScrollReveal>
              <p className="text-label mb-12">{t("teaser.headline")}</p>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <ScrollReveal delay={0}>
                <TeaserCard titleKey="teaser.offer.title" descKey="teaser.offer.desc" href="/offer" />
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <TeaserCard titleKey="teaser.work.title" descKey="teaser.work.desc" href="/work" />
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <TeaserCard titleKey="teaser.team.title" descKey="teaser.team.desc" href="/team" />
              </ScrollReveal>
              <ScrollReveal delay={300}>
                <TeaserCard titleKey="teaser.faq.title" descKey="teaser.faq.desc" href="/faq" />
              </ScrollReveal>
            </div>
          </div>
        </section>
      </main>
      <NewFooterSection />
      <WhitepaperPopup />
    </>
  );
};

export default Index;
